import {
  CHARGE_COST_APPLIES_TO,
  CHARGE_COST_TYPES,
  getChargeCostTypeLabel,
  useChargeCostDefinitions,
  useCreateChargeCostDefinition,
  useDeleteChargeCostDefinition,
  useServiceTags,
  useUpdateChargeCostDefinition,
  type ChargeCostDefinition,
  type ChargeCostAppliesToValue,
  type ChargeCostTypeValue,
  type CreateChargeCostDefinitionRequest,
  type ServiceTag,
  type UpdateChargeCostDefinitionRequest,
} from '@my/tms-api';
import {
  useOrganisationAccountingCategories,
  useOrganisationNominals,
  useOrganisationTaxRates,
} from '@my/directory-api/hooks';
import type {
  NominalTypeValue,
  OrganisationAccountingCategory,
  OrganisationNominal,
  OrganisationTaxRate,
} from '@my/directory-api';
import {
  ConfirmDialog,
  ErrorBanner,
  FormSection,
  Loading,
  SelectInput,
  Switch,
  TextInput,
  showToast,
} from '@my/ui';
import { Check, Edit, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categoryOptions } from '../configuration/lib/accountingCategoryOptions';
import { taxRateLabel, taxRateOptions, taxRateShort } from '../configuration/lib/taxRateOptions';

export interface ChargesAndCostsSectionProps {
  /** Owning organisation id; the section stays disabled while this is null. */
  organisationId: string | null;
  /**
   * The organisation uses accounting categories. On, every charge needs a
   * category (it is the last fallback a line can reach) and chooses whether
   * it follows the job.
   */
  categoriesEnabled: boolean;
}

/**
 * Accounting → Charges & Costs section on the Organisation page. Faithful
 * port of My.Tms.Old's section: a single FormSection with a "+ Add charge /
 * cost" header action and a table whose rows expand to an inline editor.
 *
 * Tags column / picker only renders when `appliesTo === 'Service'` — same
 * gating rule the BE enforces. The picker is filtered to service tags
 * whose `applyToServiceTypes` includes `ChargesAndCosts` (or is null/empty
 * = applies-everywhere); operators can't bind a charge to a tag that
 * isn't scoped to charges.
 */
export function ChargesAndCostsSection({ organisationId, categoriesEnabled }: ChargesAndCostsSectionProps) {
  const { t } = useTranslation('organisation');
  const listQuery = useChargeCostDefinitions(organisationId);
  const createMutation = useCreateChargeCostDefinition();
  const updateMutation = useUpdateChargeCostDefinition();
  const deleteMutation = useDeleteChargeCostDefinition();

  // Cross-feature read for the Tags picker.
  const serviceTagsQuery = useServiceTags(organisationId);
  // The nominal pickers choose from the chart of accounts on this page.
  const nominalsQuery = useOrganisationNominals(organisationId);
  const nominals = nominalsQuery.data ?? EMPTY_NOMINALS;
  const categoriesQuery = useOrganisationAccountingCategories(categoriesEnabled ? organisationId : null);
  // Null hides the category controls entirely.
  const accountingCategories = categoriesEnabled ? (categoriesQuery.data ?? EMPTY_CATEGORIES) : null;
  // The VAT-rate override picks from the Tax Schemas on this page.
  const taxRatesQuery = useOrganisationTaxRates(organisationId);
  const taxRates = taxRatesQuery.data ?? EMPTY_TAX_RATES;
  const taxRatesById = useMemo(() => new Map(taxRates.map((r) => [r.id, r])), [taxRates]);

  // `null` = no editor open; `'new'` = drafting; a guid = editing that row.
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<ChargeCostDraft>(emptyDraft());
  const [pendingDelete, setPendingDelete] = useState<ChargeCostDefinition | null>(null);

  // Ref to whichever editor `<tr>` is currently mounted (only one renders at
  // a time — `'new'` at the top OR the edited row in-place — so the same
  // ref points at whichever editor is open). Used to scroll the editor
  // into view on open, so clicking edit on a row near the bottom of the
  // table doesn't leave the editor below the fold. Same pattern as
  // NominalCodesSection on this page.
  const editorRowRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    if (editingId === null) return;
    // requestAnimationFrame defers the scroll until after the editor row
    // has actually mounted/painted — without this, `scrollIntoView` runs
    // against a stale layout and can no-op or land in the wrong spot.
    const id = requestAnimationFrame(() => {
      editorRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(id);
  }, [editingId]);

  const isBusy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // Service tags scoped to this section: active + applies to ChargesAndCosts
  // (or all). Empty / null `applyToServiceTypes` is the "applies everywhere"
  // sentinel — same gating the BE enforces.
  const chargeServiceTags = useMemo(
    () =>
      (serviceTagsQuery.data ?? []).filter(
        (t) =>
          t.status === 'Active' &&
          (!t.applyToServiceTypes ||
            t.applyToServiceTypes
              .split(',')
              .map((s) => s.trim())
              .includes('ChargesAndCosts')),
      ),
    [serviceTagsQuery.data],
  );

  // Set of non-Custom types already in use by ACTIVE rows. The BE enforces
  // "one row per non-Custom type per org"; the FE filters those types out
  // of the dropdown when adding a new row (or editing a Custom row that
  // could be re-classified) so the operator never picks a type that would
  // 409. Custom is never in this set — multiple Custom rows are allowed.
  const usedNonCustomTypes = useMemo(() => {
    const set = new Set<ChargeCostTypeValue>();
    for (const row of listQuery.data ?? []) {
      if (row.type !== 'Custom') set.add(row.type);
    }
    return set;
  }, [listQuery.data]);

  // Index every service tag (including non-charge ones) by id, so a row
  // that historically referenced a now-non-charge tag still renders the
  // chip name in the read-only summary instead of a bare GUID.
  const tagsById = useMemo(() => {
    const map = new Map<string, ServiceTag>();
    for (const t of serviceTagsQuery.data ?? []) map.set(t.id, t);
    return map;
  }, [serviceTagsQuery.data]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const startNew = () => {
    setEditingId('new');
    setDraft(emptyDraft());
  };

  const startEdit = (row: ChargeCostDefinition) => {
    setEditingId(row.id);
    setDraft(toDraft(row));
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(emptyDraft());
  };

  const requestDelete = (row: ChargeCostDefinition) => setPendingDelete(row);

  const handleSave = async () => {
    if (!organisationId || editingId === null) return;
    const displayName = draft.displayName.trim();
    if (!displayName) return;
    // Every line can fall back to its charge's category, which is what makes
    // the cargo / product ones optional — so while categories are on, a
    // charge must have one.
    if (categoriesEnabled && !draft.accountingCategoryCode) {
      showToast({ type: 'error', message: t('chargesAndCosts.category.required') });
      return;
    }

    // Tag list is only sent when AppliesTo === Service (BE rejects
    // non-empty otherwise — keeping FE in lock-step is one less round-trip
    // for the operator on tag mistakes).
    const serviceTagIds = draft.appliesTo === 'Service' ? [...draft.serviceTagIds] : [];

    const payload = {
      type: draft.type,
      displayName,
      appliesTo: draft.appliesTo,
      salesNominal: draft.salesNominal.trim() || null,
      purchaseNominal: draft.purchaseNominal.trim() || null,
      serviceTagIds,
      accountingCategoryCode: draft.accountingCategoryCode || null,
      categoryFollowsJob: draft.categoryFollowsJob,
      taxRateId: draft.taxRateId || null,
      vatFollowsJob: draft.vatFollowsJob,
    };

    try {
      if (editingId === 'new') {
        const body: CreateChargeCostDefinitionRequest = { organisationId, ...payload };
        await createMutation.mutateAsync(body);
        showToast({ type: 'success', message: t('chargesAndCosts.added') });
      } else {
        const body: UpdateChargeCostDefinitionRequest = { chargeCostDefinitionId: editingId, ...payload };
        await updateMutation.mutateAsync({ organisationId, body });
        showToast({ type: 'success', message: t('chargesAndCosts.updated') });
      }
      cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('chargesAndCosts.saveFailed'),
      });
    }
  };

  const confirmDeletePending = async () => {
    const row = pendingDelete;
    if (!row || !organisationId) return;
    try {
      await deleteMutation.mutateAsync({ organisationId, id: row.id });
      showToast({ type: 'success', message: t('chargesAndCosts.removed') });
      if (editingId === row.id) cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('chargesAndCosts.deleteFailed'),
      });
    } finally {
      setPendingDelete(null);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  // Hide +Add while an editor is open — no sense starting a second draft.
  const headerAction =
    listQuery.isSuccess && editingId === null ? (
      <button
        type="button"
        aria-label={t('chargesAndCosts.addAria')}
        onClick={startNew}
        disabled={!organisationId || isBusy}
        className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
      >
        <Plus className="h-3.5 w-3.5" />
        {t('chargesAndCosts.add')}
      </button>
    ) : null;

  const sortedRows = listQuery.data ?? [];

  return (
    <FormSection title={t('chargesAndCosts.title')} columns={1} actions={headerAction}>
      {listQuery.isPending && <Loading label={t('chargesAndCosts.loading')} />}
      {listQuery.isError && (
        <ErrorBanner
          title={t('chargesAndCosts.loadFailed')}
          message={listQuery.error.message}
        />
      )}

      {listQuery.isSuccess && (
        <div className="overflow-auto">
          {/* `overflow-hidden rounded-md` clips the dark thead bar to the
              rounded shape. No outer `border` — Tax Schemas / Nominal
              Codes don't have one either, and the dark thead + tbody
              `divide-y` give enough structure on their own. */}
          <div className="min-w-max overflow-hidden rounded-md">
            <table className="w-full table-auto text-xs">
              <thead className="bg-slate-700 text-[11px] uppercase tracking-wide text-white">
                <tr>
                  <Th>{t('chargesAndCosts.table.display')}</Th>
                  <Th>{t('chargesAndCosts.table.type')}</Th>
                  <Th>{t('chargesAndCosts.table.appliesTo')}</Th>
                  <Th align="center">{t('chargesAndCosts.table.nominals')}</Th>
                  <Th>{t('chargesAndCosts.table.tags')}</Th>
                  <Th align="right">{t('chargesAndCosts.table.actions')}</Th>
                </tr>
              </thead>
              {/* `divide-y divide-slate-500` for horizontal-only row
                  separators — same as Tax Schemas. No vertical lines. */}
              <tbody className="divide-y divide-slate-500 bg-white">
                {/* New-row editor pinned at top so the +Add click flows the
                    user's eye straight to the editor (legacy behaviour).
                    `max-w-2xl` caps the editor on the left half of the row
                    — same sizing as NominalCodesSection so the page reads
                    consistently. */}
                {editingId === 'new' && (
                  <tr ref={editorRowRef}>
                    <td colSpan={6} className="bg-slate-50 px-3 py-3">
                      <div className="max-w-2xl">
                        <ChargeCostEditor
                          draft={draft}
                          isNew
                          isBusy={isBusy}
                          onChange={setDraft}
                          onSave={handleSave}
                          onCancel={cancel}
                          availableServiceTags={chargeServiceTags}
                          usedNonCustomTypes={usedNonCustomTypes}
                          nominals={nominals}
                          accountingCategories={accountingCategories}
                          taxRates={taxRates}
                        />
                      </div>
                    </td>
                  </tr>
                )}

                {sortedRows.map((row) => {
                  const isEditing = editingId === row.id;
                  return (
                    <ChargeCostRow
                      key={row.id}
                      row={row}
                      isEditing={isEditing}
                      rowsLocked={editingId !== null && !isEditing}
                      onClick={() => {
                        if (editingId === null && !isBusy) startEdit(row);
                      }}
                      onEditClick={() => startEdit(row)}
                      tagsById={tagsById}
                      categoriesEnabled={categoriesEnabled}
                      taxRatesById={taxRatesById}
                    >
                      {isEditing && (
                        <tr ref={editorRowRef}>
                          <td colSpan={6} className="bg-slate-50 px-3 py-3">
                            <div className="max-w-2xl">
                              <ChargeCostEditor
                                draft={draft}
                                isNew={false}
                                isBusy={isBusy}
                                onChange={setDraft}
                                onSave={handleSave}
                                onCancel={cancel}
                                onDelete={row.isProtected ? undefined : () => requestDelete(row)}
                                availableServiceTags={chargeServiceTags}
                                usedNonCustomTypes={usedNonCustomTypes}
                                nominals={nominals}
                                accountingCategories={accountingCategories}
                                taxRates={taxRates}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </ChargeCostRow>
                  );
                })}

                {sortedRows.length === 0 && editingId !== 'new' && (
                  <tr>
                    <td colSpan={6} className="px-3 py-4 text-center text-xs italic text-slate-400">
                      {t('chargesAndCosts.empty', {
                        add: t('chargesAndCosts.addAction'),
                      })}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('chargesAndCosts.deleteDialog.title')}
        message={
          pendingDelete
            ? t('chargesAndCosts.deleteDialog.message', { name: pendingDelete.displayName })
            : null
        }
        confirmLabel={t('chargesAndCosts.deleteDialog.confirm')}
        onConfirm={confirmDeletePending}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </FormSection>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Read-only display row
// ────────────────────────────────────────────────────────────────────────────

function Th({ children, align }: { children: React.ReactNode; align?: 'right' | 'center' }) {
  // Casing / tracking / size / colour come from the parent <thead>; this
  // helper just owns padding + alignment + weight so column headers stay
  // consistent. Matches the Tax Schemas reference styling.
  return (
    <th
      className={`px-3 py-2 font-semibold ${
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}

interface ChargeCostRowProps {
  row: ChargeCostDefinition;
  isEditing: boolean;
  rowsLocked: boolean;
  onClick: () => void;
  onEditClick: () => void;
  tagsById: Map<string, ServiceTag>;
  /** Show the charge's category (or its absence) beside the nominals. */
  categoriesEnabled: boolean;
  taxRatesById: Map<string, OrganisationTaxRate>;
  children?: React.ReactNode;
}

function ChargeCostRow({
  row,
  isEditing,
  rowsLocked,
  onClick,
  onEditClick,
  tagsById,
  categoriesEnabled,
  taxRatesById,
  children,
}: ChargeCostRowProps) {
  const { t } = useTranslation('organisation');
  // Row separator comes from the tbody's `divide-y divide-slate-500`.
  // No per-row border or background switching — keeps the grid clean.
  const rowClass = isEditing
    ? 'bg-slate-50'
    : rowsLocked
      ? 'opacity-50'
      : 'cursor-pointer hover:bg-slate-50';

  return (
    <>
      {/* Double-click opens the editor (single-click would interfere with
          text selection in the row). The Edit button still works on a
          single click for touch / keyboard. */}
      <tr
        className={rowClass}
        onDoubleClick={rowsLocked || isEditing ? undefined : onClick}
      >
        <td className="whitespace-nowrap px-3 py-0.5 font-semibold text-slate-900">
          {row.displayName}
        </td>
        <td className="whitespace-nowrap px-3 py-0.5 text-slate-500">
          {getChargeCostTypeLabel(row.type)}
        </td>
        <td className="whitespace-nowrap px-3 py-0.5 text-slate-500">{row.appliesTo}</td>
        <td className="whitespace-nowrap px-3 py-0.5 text-center tabular-nums text-slate-500">
          <span>{row.salesNominal || '—'}</span>
          <span className="mx-1 text-slate-300">|</span>
          <span>{row.purchaseNominal || '—'}</span>
          {categoriesEnabled &&
            (row.accountingCategoryCode ? (
              <span
                className="ml-2 rounded-sm bg-violet-50 px-1 py-0.5 font-mono text-[10px] font-semibold text-violet-700"
                title={
                  row.categoryFollowsJob
                    ? t('chargesAndCosts.category.followsJobOn')
                    : t('chargesAndCosts.category.followsJobOff')
                }
              >
                {row.accountingCategoryCode}
                {row.categoryFollowsJob && <span className="ml-1 font-sans font-normal">↻</span>}
              </span>
            ) : (
              <span className="ml-2 rounded-sm bg-amber-50 px-1 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
                {t('chargesAndCosts.category.missing')}
              </span>
            ))}
          {row.taxRateId && (
            <span
              className="ml-2 rounded-sm bg-sky-50 px-1 py-0.5 text-[10px] font-semibold text-sky-700"
              title={t('chargesAndCosts.vat.badgeTitle', {
                rate: taxRatesById.get(row.taxRateId)
                  ? taxRateLabel(taxRatesById.get(row.taxRateId)!)
                  : t('chargesAndCosts.vat.notInList'),
              })}
            >
              {taxRatesById.get(row.taxRateId) ? taxRateShort(taxRatesById.get(row.taxRateId)!) : '?'} VAT
              {row.vatFollowsJob && <span className="ml-1 font-normal">↻</span>}
            </span>
          )}
        </td>
        <td className="px-3 py-0.5 text-slate-500">
          <div className="flex flex-wrap gap-1">
            {row.serviceTagIds.length === 0 ? (
              row.appliesTo === 'Service' ? (
                <span className="italic text-slate-300">—</span>
              ) : null
            ) : (
              row.serviceTagIds.map((id) => {
                const tag = tagsById.get(id);
                return (
                  <span
                    key={id}
                    className="inline-flex items-center whitespace-nowrap rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700"
                  >
                    {tag?.name ?? id}
                  </span>
                );
              })
            )}
          </div>
        </td>
        <td className="whitespace-nowrap px-3 py-0.5 text-right">
          {/* `inline-flex` so the button respects the cell's `text-right` —
              block-level `flex` would pin to the cell's left edge. */}
          <button
            type="button"
            aria-label={t('chargesAndCosts.ariaEdit', { name: row.displayName })}
            onClick={(e) => {
              e.stopPropagation();
              if (!rowsLocked) onEditClick();
            }}
            disabled={rowsLocked}
            className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
          >
            <Edit className="h-3.5 w-3.5" />
          </button>
        </td>
      </tr>
      {children}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Inline editor
// ────────────────────────────────────────────────────────────────────────────

interface ChargeCostDraft {
  /** Empty for a brand-new draft, populated when editing an existing row. */
  id: string | null;
  type: ChargeCostTypeValue;
  displayName: string;
  appliesTo: ChargeCostAppliesToValue;
  salesNominal: string;
  purchaseNominal: string;
  serviceTagIds: string[];
  /** `''` = none. */
  accountingCategoryCode: string;
  categoryFollowsJob: boolean;
  /** `''` = the VAT treatment's rate. */
  taxRateId: string;
  vatFollowsJob: boolean;
  /** Mirrors entity flag — locks the AppliesTo control on protected rows. */
  isProtected: boolean;
  /** Mirrors entity flag — locks the Type control on system rows. */
  isSystem: boolean;
}

function emptyDraft(): ChargeCostDraft {
  return {
    id: null,
    type: 'Custom',
    displayName: '',
    appliesTo: 'Orders',
    salesNominal: '',
    purchaseNominal: '',
    serviceTagIds: [],
    accountingCategoryCode: '',
    categoryFollowsJob: false,
    taxRateId: '',
    vatFollowsJob: true,
    isProtected: false,
    isSystem: false,
  };
}

function toDraft(row: ChargeCostDefinition): ChargeCostDraft {
  return {
    id: row.id,
    type: row.type,
    displayName: row.displayName,
    appliesTo: row.appliesTo,
    salesNominal: row.salesNominal ?? '',
    purchaseNominal: row.purchaseNominal ?? '',
    serviceTagIds: [...row.serviceTagIds],
    accountingCategoryCode: row.accountingCategoryCode ?? '',
    categoryFollowsJob: row.categoryFollowsJob ?? false,
    taxRateId: row.taxRateId ?? '',
    vatFollowsJob: row.vatFollowsJob ?? true,
    isProtected: row.isProtected,
    isSystem: row.isSystem,
  };
}

interface ChargeCostEditorProps {
  draft: ChargeCostDraft;
  isNew: boolean;
  isBusy: boolean;
  onChange: (next: ChargeCostDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  /** Hidden when undefined (new row, or protected existing row). */
  onDelete?: () => void;
  availableServiceTags: ServiceTag[];
  /**
   * Non-Custom types currently in use by other ACTIVE rows. The Type
   * dropdown filters these out so the operator can't pick a type that
   * the BE would 409 on. The current row's own type is never filtered
   * (so editing a row never hides its own classification).
   */
  usedNonCustomTypes: Set<ChargeCostTypeValue>;
  /** The organisation's nominal codes — the two nominal pickers choose from these. */
  nominals: OrganisationNominal[];
  /** Null when the organisation does not use accounting categories. */
  accountingCategories: OrganisationAccountingCategory[] | null;
  /** The organisation's tax schemas — the VAT-rate override chooses from these. */
  taxRates: OrganisationTaxRate[];
}

function ChargeCostEditor({
  draft,
  isNew,
  isBusy,
  onChange,
  onSave,
  onCancel,
  onDelete,
  availableServiceTags,
  usedNonCustomTypes,
  nominals,
  accountingCategories,
  taxRates,
}: ChargeCostEditorProps) {
  const { t } = useTranslation('organisation');
  const lockType = draft.isSystem || draft.isProtected; // system / protected can't be re-typed
  const lockAppliesTo = draft.isProtected; // only protected locks AppliesTo
  const showTags = draft.appliesTo === 'Service';

  const setField = <K extends keyof ChargeCostDraft>(key: K, value: ChargeCostDraft[K]) =>
    onChange({ ...draft, [key]: value });

  const handleTypeChange = (raw: string) => {
    const next = raw as ChargeCostTypeValue;
    // Auto-populate Display with the type label so the operator gets a
    // sensible default (legacy did this too). They can override.
    onChange({
      ...draft,
      type: next,
      displayName: getChargeCostTypeLabel(next),
      // Transport and fuel follow the job by default; the rest do not.
      categoryFollowsJob: next === 'TransportCharge' || next === 'FuelSurcharge',
    });
  };

  const handleAppliesToChange = (raw: string) => {
    const next = raw as ChargeCostAppliesToValue;
    // Switching away from Service drops the tag selection — those links
    // no longer make sense (BE Update handler does the same).
    onChange({
      ...draft,
      appliesTo: next,
      serviceTagIds: next === 'Service' ? draft.serviceTagIds : [],
    });
  };

  const toggleTag = (id: string) => {
    setField(
      'serviceTagIds',
      draft.serviceTagIds.includes(id)
        ? draft.serviceTagIds.filter((t) => t !== id)
        : [...draft.serviceTagIds, id],
    );
  };

  const hasError = !draft.displayName.trim();

  // Build label-aware option lists for the dropdowns. Using SelectInput
  // (floating-label, h-9 w-full wrapper) instead of a raw <select> so the
  // controls line up with TextInput visually inside the editor grid.
  //
  // Filter out non-Custom types that are already in use by another active
  // row — the BE enforces "one row per non-Custom type per org", so
  // surfacing those options would only earn the operator a 409. Custom is
  // always offered (multiple Custom rows are allowed). The CURRENT row's
  // own type is always offered so editing a row never hides its own
  // classification (and on edit, lockType usually disables the dropdown
  // entirely anyway).
  const typeOptions = CHARGE_COST_TYPES
    .filter((t) => t === 'Custom' || t === draft.type || !usedNonCustomTypes.has(t))
    .map((t) => ({
      value: t,
      label: getChargeCostTypeLabel(t),
    }));
  const appliesToOptions = CHARGE_COST_APPLIES_TO.map((a) => ({
    value: a,
    label: a,
  }));

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      {/* Type + Display */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {lockType ? (
          // Locked state: render as a TextInput so the height + border
          // match the editable Display field next to it. Soft disabled
          // styling (slate-50 bg) is baked into TextInput's `read-only:`
          // variants — no explicit override needed.
          <TextInput
            label={t('chargesAndCosts.fields.type')}
            name="type"
            value={getChargeCostTypeLabel(draft.type)}
            readOnly
          />
        ) : (
          <SelectInput
            label={t('chargesAndCosts.fields.type')}
            name="type"
            value={draft.type}
            onChange={(e) => handleTypeChange(e.target.value)}
            disabled={isBusy}
            options={typeOptions}
          />
        )}

        <TextInput
          label={t('chargesAndCosts.fields.display')}
          name="displayName"
          value={draft.displayName}
          maxLength={128}
          onChange={(e) => setField('displayName', e.target.value)}
          error={hasError}
          errorMessage={hasError ? t('chargesAndCosts.validation.displayRequired') : ''}
        />
      </div>

      {/* AppliesTo + Nominals */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {lockAppliesTo ? (
          // Soft disabled styling baked into TextInput — no override needed.
          <TextInput
            label={t('chargesAndCosts.fields.appliesTo')}
            name="appliesTo"
            value={draft.appliesTo}
            readOnly
          />
        ) : (
          <SelectInput
            label={t('chargesAndCosts.fields.appliesTo')}
            name="appliesTo"
            value={draft.appliesTo}
            onChange={(e) => handleAppliesToChange(e.target.value)}
            disabled={isBusy}
            options={appliesToOptions}
          />
        )}

        {/* Picked from Nominal Codes (same page) rather than typed: a typo
            here used to reach the invoice line, and from there the accounts. */}
        <SelectInput
          label={t('chargesAndCosts.fields.salesNominal')}
          name="salesNominal"
          value={draft.salesNominal}
          onChange={(e) => setField('salesNominal', e.target.value)}
          disabled={isBusy}
          options={nominalOptions(nominals, 'Sales', draft.salesNominal, t)}
        />
        <SelectInput
          label={t('chargesAndCosts.fields.purchaseNominal')}
          name="purchaseNominal"
          value={draft.purchaseNominal}
          onChange={(e) => setField('purchaseNominal', e.target.value)}
          disabled={isBusy}
          options={nominalOptions(nominals, 'Purchase', draft.purchaseNominal, t)}
        />
      </div>

      {/* One line each: the value and its own "follows the job" switch beside
          it — separate switches, because waiting time takes the transport's
          VAT even when its category is fixed. Category only while the
          organisation uses them. Same three columns as the nominals above. */}
      {accountingCategories && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <SelectInput
            label={t('chargesAndCosts.category.label')}
            name="accountingCategoryCode"
            value={draft.accountingCategoryCode}
            onChange={(e) => setField('accountingCategoryCode', e.target.value)}
            disabled={isBusy}
            error={!draft.accountingCategoryCode}
            errorMessage={t('chargesAndCosts.category.required')}
            options={categoryOptions(
              accountingCategories,
              draft.accountingCategoryCode,
              t('chargesAndCosts.category.choose'),
              (code) => t('chargesAndCosts.category.notInList', { code }),
            )}
          />
          <div className="md:col-span-2">
            <Switch
              label={t('chargesAndCosts.category.followsJob')}
              name="categoryFollowsJob"
              checked={draft.categoryFollowsJob}
              enabledLabel={t('chargesAndCosts.category.followsJobOn')}
              disabledLabel={t('chargesAndCosts.category.followsJobOff')}
              onChange={(e) => setField('categoryFollowsJob', e.currentTarget.checked)}
              disabled={isBusy}
            />
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <SelectInput
          label={t('chargesAndCosts.vat.label')}
          name="taxRateId"
          value={draft.taxRateId}
          onChange={(e) => setField('taxRateId', e.target.value)}
          disabled={isBusy}
          title={t('chargesAndCosts.vat.hint')}
          options={taxRateOptions(
            taxRates,
            draft.taxRateId,
            t('chargesAndCosts.vat.fromTreatment'),
            t('chargesAndCosts.vat.notInList'),
          )}
        />
        <div className="md:col-span-2">
          <Switch
            label={t('chargesAndCosts.vat.followsJob')}
            name="vatFollowsJob"
            checked={draft.vatFollowsJob}
            enabledLabel={t('chargesAndCosts.vat.followsJobOn')}
            disabledLabel={t('chargesAndCosts.vat.followsJobOff')}
            onChange={(e) => setField('vatFollowsJob', e.currentTarget.checked)}
            disabled={isBusy}
          />
        </div>
      </div>

      {/* Tags — only when AppliesTo === Service */}
      {showTags && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-slate-600">{t('chargesAndCosts.fields.tags')}</p>
          {availableServiceTags.length === 0 ? (
            <p className="text-xs italic text-slate-400">
              {t('chargesAndCosts.fields.noTags')}
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {availableServiceTags.map((tag) => {
                const active = draft.serviceTagIds.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.id)}
                    disabled={isBusy}
                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-50 ${
                      active
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-slate-300 bg-white text-slate-600 hover:border-blue-400 hover:text-blue-700'
                    }`}
                  >
                    {tag.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between gap-2 pt-1">
        <div>
          {!isNew && onDelete && (
            <button
              type="button"
              aria-label={t('chargesAndCosts.ariaDelete')}
              onClick={onDelete}
              disabled={isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Protected label — surfaces why Delete is hidden / AppliesTo is locked. */}
        {draft.isProtected && (
          <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
            {t('chargesAndCosts.protectedBadge')}
          </span>
        )}

        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t('chargesAndCosts.ariaCancel')}
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={t('chargesAndCosts.ariaSave')}
            onClick={onSave}
            disabled={isBusy || hasError}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

const EMPTY_NOMINALS: OrganisationNominal[] = [];
const EMPTY_CATEGORIES: OrganisationAccountingCategory[] = [];
const EMPTY_TAX_RATES: OrganisationTaxRate[] = [];

/**
 * Options for a nominal picker: "None", then the organisation's ACTIVE
 * nominals of that type as "code — description". The stored value is the
 * nominal's CODE — the short identifier the Nominal Codes table describes as
 * the thing charges are wired to, and what invoice generation copies onto
 * the line.
 *
 * <p>
 * A value already stored that is not in that list (typed free-hand before
 * this was a picker, or a nominal since made inactive or deleted) is kept as
 * an extra, flagged option — otherwise the select would show "None" and the
 * next save would silently wipe it.
 * </p>
 */
function nominalOptions(
  nominals: OrganisationNominal[],
  type: NominalTypeValue,
  current: string,
  t: (key: 'chargesAndCosts.fields.nominalNone' | 'chargesAndCosts.fields.nominalNotInList', opts?: { code: string }) => string,
) {
  const active = nominals
    .filter((n) => n.type === type && n.status === 'Active')
    .sort((a, b) => a.displayOrder - b.displayOrder || a.code.localeCompare(b.code));
  const options = [
    { value: '', label: t('chargesAndCosts.fields.nominalNone') },
    ...active.map((n) => ({ value: n.code, label: `${n.code} — ${n.description}` })),
  ];
  const trimmed = current.trim();
  if (trimmed && !active.some((n) => n.code === trimmed)) {
    options.push({
      value: current,
      label: t('chargesAndCosts.fields.nominalNotInList', { code: trimmed }),
    });
  }
  return options;
}
